import { useEffect, useState } from "react";
import classes from "../../PaymentMethods.module.scss";
import braintree, { PayPal } from "braintree-web";
import { trackError } from "@/utils/error-monitoring";
import { useDonationFormStore } from "@/stores";
import { getAsset } from "@/utils/assets";
import { getCountryName } from "@/utils/countries";
import { CURRENCY } from "@/config";
import type { WalletButtonProps } from "@/payment/types";
import { PaymentPopupBlockedError } from "@/payment/PaymentPopupBlockedError";
import { useBraintree } from "./useBraintree";

export function BraintreePayPalButton(props: Readonly<WalletButtonProps>) {
  const { amount, onSuccess, onError } = props;
  const { braintreeClient } = useBraintree();

  const [isLoading, setIsLoading] = useState(true);
  const [payPalInstance, setPayPalInstance] = useState<PayPal>();
  const {
    setGatewayPayload,
    setAddress,
    setIsManualAddress,
    setPersonalDetails,
  } = useDonationFormStore();

  useEffect(() => {
    if (!braintreeClient) return;

    const initializePayPalPay = async () => {
      try {
        const payPalInstance = await braintree.paypal.create({
          client: braintreeClient,
        });
        setPayPalInstance(payPalInstance);
      } catch (error) {
        trackError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initializePayPalPay();
  }, [braintreeClient]);

  const handlePayPalPayment = async () => {
    if (!payPalInstance) return;

    try {
      const payload = await payPalInstance.tokenize({
        flow: "checkout",
        amount: amount,
        currency: CURRENCY,
        enableShippingAddress: true,
      });

      if (!payload.nonce) {
        throw new Error("No nonce found");
      }

      setGatewayPayload({ nonce: payload.nonce });
      setAddress({
        "address-line-1": payload.details.billingAddress?.line1 || "",
        "address-line-2": payload.details.billingAddress?.line2 || "",
        city: payload.details.billingAddress?.city || "",
        region: payload.details.billingAddress?.state || "",
        "postal-code": payload.details.billingAddress?.postalCode || "",
        country: getCountryName(payload.details.billingAddress?.countryCode),
      });

      setPersonalDetails({
        title: "",
        firstName: payload.details.firstName || "",
        lastName: payload.details.lastName || "",
        email: payload.details.email || "",
        confirmEmail: payload.details.email || "",
      });

      setIsManualAddress(true);
      onSuccess({ nonce: payload.nonce });
    } catch (error) {
      const code = (error as { code?: string })?.code;

      if (code === "PAYPAL_POPUP_CLOSED") return;

      // A second click while the PayPal window is still open: the first
      // request carries on, so there is nothing to tell the donor.
      if (code === "PAYPAL_TOKENIZATION_REQUEST_ACTIVE") return;

      // A pop-up blocker or content blocker refused the window: nothing is
      // wrong in our code, but the donor needs to know why nothing happened.
      if (code === "PAYPAL_POPUP_OPEN_FAILED") {
        trackError(error as Error, { level: "warning" });
        onError?.(new PaymentPopupBlockedError());
        return;
      }

      trackError(error as Error);
      onError?.(error as Error);
    }
  };

  return (
    <button
      type="button"
      className={classes.button}
      onClick={handlePayPalPayment}
      disabled={isLoading}
    >
      <span>Donate with</span>
      <img
        src={getAsset("/images/paypal.svg")}
        className={classes.icon}
        width={907}
        height={245}
        alt="PayPal"
      />
    </button>
  );
}
